/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import LegalDocumentModal from "../../components/legal/LegalDocumentModal";
import { LEGAL_DOCUMENTS, loadLegalDocument } from "../../utils/legalDocuments";

export default function SignupUI({
  COLORS,
  isArabic,
  isMobile,
  form,
  errors,
  passwordStrength,
  getStrengthColor,
  getStrengthValue,
  loading,
  iraqFlag,
  handleChange,
  handleSubmit,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [activeLegalDoc, setActiveLegalDoc] = useState("privacy");
  const [legalContent, setLegalContent] = useState("");
  const [legalLoading, setLegalLoading] = useState(false);
  const TEXT_COLOR = "#E0E0E0";

  const openLegalDoc = async (docKey) => {
    setActiveLegalDoc(docKey);
    setShowTermsModal(true);
    setLegalContent("");
    setLegalLoading(true);

    try {
      const text = await loadLegalDocument(docKey, isArabic ? "ar" : "en");
      setLegalContent(text);
    } catch (error) {
      setLegalContent(
        isArabic ? "تعذر تحميل المستند حالياً." : "Unable to load the document right now."
      );
    } finally {
      setLegalLoading(false);
    }
  };

  return (
    <div
      style={{
        background: COLORS.gradient,
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "30px",
        fontFamily: "'Tajawal', 'Cairo', sans-serif",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <div
        style={{
          maxWidth: isMobile ? "100%" : 450,
          width: "100%",
          background: "rgba(255,255,255,0.08)",
          borderRadius: isMobile ? 14 : 18,
          padding: isMobile ? "25px 15px" : "40px 30px",
          boxShadow: "0 8px 25px rgba(0,0,0,0.3)",
          border: "1px solid rgba(255,255,255,0.15)",
          backdropFilter: "blur(12px)",
          color: "#fff",
        }}
      >
        <h3 className="fw-bold text-center mb-3" style={{ color: COLORS.gold }}>
          {isArabic ? "إنشاء حساب" : "Create Account"}
        </h3>

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label fw-semibold" style={{ fontSize: isMobile ? "0.9rem" : "1rem" }}>
              {isArabic ? "الاسم الكامل (بالعربية)" : "Full Name (Arabic)"}
            </label>
            <input
              name="fullName"
              className={`form-control ${errors.fullName ? "is-invalid" : ""}`}
              value={form.fullName}
              onChange={handleChange}
              style={{
                background: "rgba(255,255,255,0.15)",
                color: TEXT_COLOR,
                border: "1px solid rgba(255,255,255,0.3)",
                fontSize: isMobile ? "0.9rem" : "1rem",
                padding: isMobile ? "8px 12px" : "10px 15px",
              }}
            />
            {errors.fullName && <small className="text-warning" style={{ fontSize: isMobile ? "0.8rem" : "0.9rem" }}>{errors.fullName}</small>}
          </div>

          <div className="mb-3">
            <label className="form-label fw-semibold" style={{ fontSize: isMobile ? "0.9rem" : "1rem" }}>
              {isArabic ? "الاسم الكامل (بالإنجليزية)" : "Full Name (English)"}
            </label>
            <input
              name="engFullName"
              className={`form-control ${errors.engFullName ? "is-invalid" : ""}`}
              value={form.engFullName}
              onChange={handleChange}
              style={{
                background: "rgba(255,255,255,0.15)",
                color: TEXT_COLOR,
                border: "1px solid rgba(255,255,255,0.3)",
                fontSize: isMobile ? "0.9rem" : "1rem",
                padding: isMobile ? "8px 12px" : "10px 15px",
              }}
            />
            {errors.engFullName && <small className="text-warning" style={{ fontSize: isMobile ? "0.8rem" : "0.9rem" }}>{errors.engFullName}</small>}
          </div>

          <div className="mb-3">
            <label className="form-label fw-semibold" style={{ fontSize: isMobile ? "0.9rem" : "1rem" }}>
              {isArabic ? "رقم الهاتف" : "Phone Number"}
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: isMobile ? "wrap" : "nowrap" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  background: "rgba(255,255,255,0.2)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  borderRadius: "8px",
                  padding: isMobile ? "5px 8px" : "6px 10px",
                  minWidth: isMobile ? "70px" : "80px",
                  justifyContent: "center",
                }}
              >
                <img src={iraqFlag} alt="Iraq" style={{ width: isMobile ? 18 : 22, height: isMobile ? 12 : 15, borderRadius: 2 }} />
                <span style={{ fontWeight: 600, color: TEXT_COLOR, fontSize: isMobile ? "0.85rem" : "0.95rem" }}>+964</span>
              </div>
              <input
                name="phone"
                className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                value={form.phone}
                onChange={handleChange}
                style={{
                  background: "rgba(255,255,255,0.15)",
                  color: TEXT_COLOR,
                  border: "1px solid rgba(255,255,255,0.3)",
                  fontSize: isMobile ? "0.9rem" : "1rem",
                  padding: isMobile ? "8px 12px" : "10px 15px",
                  flex: 1,
                }}
              />
            </div>
            {errors.phone && <small className="text-warning" style={{ fontSize: isMobile ? "0.8rem" : "0.9rem" }}>{errors.phone}</small>}
          </div>

          <div className="mb-3">
            <label className="form-label fw-semibold" style={{ fontSize: isMobile ? "0.9rem" : "1rem" }}>
              {isArabic ? "البريد الإلكتروني" : "Email"}
            </label>
            <input
              name="email"
              type="email"
              className={`form-control ${errors.email ? "is-invalid" : ""}`}
              value={form.email}
              onChange={handleChange}
              style={{
                background: "rgba(255,255,255,0.15)",
                color: TEXT_COLOR,
                border: "1px solid rgba(255,255,255,0.3)",
                fontSize: isMobile ? "0.9rem" : "1rem",
                padding: isMobile ? "8px 12px" : "10px 15px",
              }}
            />
            {errors.email && <small className="text-warning" style={{ fontSize: isMobile ? "0.8rem" : "0.9rem" }}>{errors.email}</small>}
          </div>

          <div className="mb-3">
            <label className="form-label fw-semibold" style={{ fontSize: isMobile ? "0.9rem" : "1rem" }}>
              {isArabic ? "كلمة السر" : "Password"}
            </label>
            <div style={{ position: "relative" }}>
              <input
                name="password"
                type={showPassword ? "text" : "password"}
                className={`form-control ${errors.password ? "is-invalid" : ""}`}
                value={form.password}
                onChange={handleChange}
                style={{
                  background: "rgba(255,255,255,0.15)",
                  color: TEXT_COLOR,
                  border: "1px solid rgba(255,255,255,0.3)",
                  fontSize: isMobile ? "0.9rem" : "1rem",
                  padding: isMobile ? "8px 12px" : "10px 15px",
                }}
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  top: "50%",
                  transform: "translateY(-50%)",
                  [isArabic ? "left" : "right"]: "15px",
                  cursor: "pointer",
                  color: COLORS.gold,
                  fontSize: "1.2rem",
                }}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </span>
            </div>
            {form.password && (
              <>
                <ul
                  style={{
                    fontSize: "0.8rem",
                    marginTop: "8px",
                    marginBottom: "8px",
                    lineHeight: 1.8,
                    color: "#fff",
                  }}
                >
                  <li>
                    {isArabic
                      ? "٨ أحرف على الأقل"
                      : "At least 8 characters"}
                  </li>
                  <li>
                    {isArabic
                      ? "تحتوي على حرف كبير وصغير"
                      : "Uppercase and lowercase letters"}
                  </li>
                  <li>
                    {isArabic ? "تحتوي على رقم" : "Contains a number"}
                  </li>
                  <li>
                    {isArabic
                      ? "تحتوي على رمز خاص (!@#$)"
                      : "Contains a special character (!@#$)"}
                  </li>
                </ul>

                <div
                  style={{
                    width: "100%",
                    height: "6px",
                    borderRadius: "6px",
                    background: "rgba(255,255,255,0.2)",
                    marginTop: "8px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: getStrengthValue(),
                      height: "100%",
                      background: getStrengthColor(),
                      transition: "width .3s ease",
                    }}
                  ></div>
                </div>
              </>
            )}
            {errors.password && <small className="text-warning" style={{ fontSize: isMobile ? "0.8rem" : "0.9rem" }}>{errors.password}</small>}
          </div>

          <div className="mb-3">
            <label className="form-label fw-semibold" style={{ fontSize: isMobile ? "0.9rem" : "1rem" }}>
              {isArabic ? "تأكيد كلمة السر" : "Confirm Password"}
            </label>
            <div style={{ position: "relative" }}>
              <input
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                className={`form-control ${errors.confirmPassword ? "is-invalid" : ""}`}
                value={form.confirmPassword}
                onChange={handleChange}
                style={{
                  background: "rgba(255,255,255,0.15)",
                  color: TEXT_COLOR,
                  border: "1px solid rgba(255,255,255,0.3)",
                  fontSize: isMobile ? "0.9rem" : "1rem",
                  padding: isMobile ? "8px 12px" : "10px 15px",
                }}
              />
              <span
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: "absolute",
                  top: "50%",
                  transform: "translateY(-50%)",
                  [isArabic ? "left" : "right"]: "15px",
                  cursor: "pointer",
                  color: COLORS.gold,
                  fontSize: "1.2rem",
                }}
              >
                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
              </span>
            </div>
            {errors.confirmPassword && <small className="text-warning" style={{ fontSize: isMobile ? "0.8rem" : "0.9rem" }}>{errors.confirmPassword}</small>}
          </div>

          <div className="mb-3 d-flex align-items-center">
            <input
              type="checkbox"
              id="agreeTerms"
              name="agreeTerms"
              checked={form.agreeTerms || false}
              onChange={handleChange}
              style={{
                width: "18px",
                height: "18px",
                cursor: "pointer",
                marginRight: isArabic ? 0 : "10px",
                marginLeft: isArabic ? "10px" : 0,
                accentColor: COLORS.gold,
              }}
            />
            <label
              htmlFor="agreeTerms"
              className="form-label mb-0"
              style={{ fontSize: isMobile ? "0.85rem" : "0.95rem", color: TEXT_COLOR }}
            >
              {isArabic ? "أوافق على " : "I agree to "}
              <span
                onClick={(e) => {
                  e.preventDefault();
                  openLegalDoc("privacy");
                }}
                style={{
                  color: COLORS.gold,
                  cursor: "pointer",
                  textDecoration: "underline"
                }}
              >
                {isArabic ? "سياسة الخصوصية" : "Privacy Policy"}
              </span>
              <span> {isArabic ? "و" : "and"} </span>
              <span
                onClick={(e) => {
                  e.preventDefault();
                  openLegalDoc("terms");
                }}
                style={{
                  color: COLORS.gold,
                  cursor: "pointer",
                  textDecoration: "underline"
                }}
              >
                {isArabic ? "الشروط والأحكام" : "Terms and Conditions"}
              </span>
            </label>
          </div>
          {errors.agreeTerms && (
            <small className="text-warning d-block mb-3" style={{ fontSize: isMobile ? "0.8rem" : "0.9rem" }}>
              {errors.agreeTerms}
            </small>
          )}

          <button
            type="submit"
            className="btn w-100 fw-semibold py-2 mt-3"
            style={{
              background: COLORS.gold,
              color: COLORS.darkPurple,
              borderRadius: 10,
              boxShadow: "0 4px 15px rgba(242,180,65,0.4)",
              transition: "0.3s",
              fontSize: isMobile ? "0.95rem" : "1rem",
              padding: isMobile ? "10px" : "12px",
            }}
            disabled={loading}
          >
            {loading
              ? isArabic
                ? "جارٍ الإنشاء..."
                : "Creating..."
              : isArabic
                ? "إنشاء حساب"
                : "Create Account"}
          </button>

          <p className="text-center mt-3" style={{ color: "#ddd", fontSize: isMobile ? "0.85rem" : "0.95rem" }}>
            {isArabic ? "لديك حساب بالفعل؟ " : "Already have an account? "}
            <a href="/login" className="fw-semibold" style={{ color: COLORS.gold, textDecoration: "none", fontSize: isMobile ? "0.85rem" : "0.95rem" }}>
              {isArabic ? "تسجيل الدخول" : "Sign In"}
            </a>
          </p>


        </form>
      </div>

      <LegalDocumentModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        title={
          isArabic
            ? LEGAL_DOCUMENTS[activeLegalDoc].titleAr
            : LEGAL_DOCUMENTS[activeLegalDoc].titleEn
        }
        content={legalContent}
        loading={legalLoading}
        isArabic={isArabic}
        themeColors={COLORS}
      />
    </div>
  );
}
